import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPen, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { format } from "date-fns";

import trashIcon from "@/assets/icons/trashIcon.svg?url";

import { Icon, LoadingOverlay, Modal, PermissionCheck } from "@/components";

import { useTableSearch } from "@/utils";

import { PermissionRoles } from "@/enum";

import { Table, TableColumn } from "@/components/Table/Table";

import { ButtonColor } from "@/components/Button/types";

import {
	useDeleteMessageTemplateMutation,
	useGetMessageTemplatesQuery,
} from "@/pages/Private/redux/messageTemplate/messageTemplate.api";
import { MessageTemplate } from "@/pages/Private/pages/Messages/schema/message-template.schema";

import { userInitials } from "@/utils/userName";

import { UpdateReplyTemplateModal } from "./UpdateReplyTemplateModal";

export const RepliesTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`templates.replies.${key}`), [t]);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
	const [selectedReplyTemplate, setSelectedReplyTemplate] = useState<MessageTemplate | null>(null);
	const { page, setPage, limit, setLimit } = useTableSearch();
	const [deleteMessageTemplate, { isLoading }] = useDeleteMessageTemplateMutation();

	const { data, isFetching } = useGetMessageTemplatesQuery({ page, limit });

	const handleDeleteModal = (row: MessageTemplate) => {
		setShowDeleteModal(true);
		setSelectedReplyTemplate(row);
	};

	const handleDeletePrompt = async () => {
		if (selectedReplyTemplate?.id) {
			await deleteMessageTemplate(selectedReplyTemplate.id).unwrap();
			setShowDeleteModal(false);
			setSelectedReplyTemplate(null);
		}
	};

	const columns: TableColumn[] = useMemo(
		() => [
			{
				title: ts("name"),
				uid: "name",
				render: (row: MessageTemplate) => <span className="font-medium">{row.name}</span>,
			},
			{
				title: ts("message"),
				uid: "message",
				render: (row: MessageTemplate) => <span>{row.content}</span>,
			},
			{
				title: ts("createdBy"),
				uid: "createdBy",
				render: (row: MessageTemplate) => (
					<div className="flex flex-row w-fit items-center">
						<div className="flex text-xs mr-2 text-white min-w-[26px] w-[26px] max-w-[26px] h-[26px] items-center justify-center rounded-full bg-brand-light p-1 cursor-pointer">
							{userInitials(row.createdBy)}
						</div>
						<div>{row.createdAt ? format(row.createdAt, "HH:mm dd.MM.yyyy") : ""}</div>
					</div>
				),
			},
			{
				title: ts("lastModifiedBy"),
				uid: "updatedBy",
				render: (row: MessageTemplate) => (
					<div className="flex">
						{row.updatedBy && (
							<div className="flex flex-row w-fit items-center">
								<div className="flex text-xs mr-2 text-white min-w-[26px] w-[26px] max-w-[26px] h-[26px] items-center justify-center rounded-full bg-brand-light p-1 cursor-pointer">
									{userInitials(row.updatedBy)}
								</div>
								<div>{row.updatedAt ? format(row.updatedAt, "HH:mm dd.MM.yyyy") : ""}</div>
							</div>
						)}
					</div>
				),
			},
			{
				title: "",
				uid: "actions",
				render: (row: MessageTemplate) => (
					<div className="flex items-center gap-2 justify-end">
						<PermissionCheck requiredPermissions={[PermissionRoles.TEMPLATES]}>
							<button
								className="cursor-pointer"
								onClick={() => {
									setSelectedReplyTemplate(row);
									setShowUpdateModal(true);
								}}
							>
								<Icon className="text-text-meta w-4 h-4" icon={faPen} size="lg" />
							</button>
						</PermissionCheck>
						<PermissionCheck requiredPermissions={[PermissionRoles.TEMPLATES]}>
							<button className="cursor-pointer" onClick={() => handleDeleteModal(row)}>
								<img alt="trash" src={trashIcon} />
							</button>
						</PermissionCheck>
					</div>
				),
			},
		],
		[ts, handleDeleteModal]
	);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	return (
		<div className="flex flex-col gap-4 w-full">
			{isFetching && <LoadingOverlay />}
			<div className="w-full">
				<div className="w-full">
					<Table
						columns={columns}
						data={data?.data ?? []}
						handleChangeRowsPerPage={handleChangeRowsPerPage}
						handleFetchPage={(page) => {
							setPage(page);
						}}
						hidePagination={false}
						limit={limit}
						overallCount={data?.meta?.itemCount}
						page={page}
						sticky={false}
						totalPages={data?.meta?.pageCount}
					/>
				</div>
			</div>
			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={handleDeletePrompt}
					isLoading={isLoading}
					isOpened={showDeleteModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={"Delete"}
					title={"Delete Reply Template"}
				>
					<div className="flex flex-row gap-4 items-start p-2 justify-start text-sm mb-2 text-text border border-status-warning rounded-md">
						<Icon className="text-status-warning w-4 h-4" icon={faTriangleExclamation} />
						You will be unable to use this reply template your inbox.
					</div>
				</Modal>
			)}
			{showUpdateModal && selectedReplyTemplate && (
				<UpdateReplyTemplateModal
					isOpen={showUpdateModal}
					prompt={selectedReplyTemplate}
					setIsOpen={setShowUpdateModal}
				/>
			)}
		</div>
	);
};
